import { soundService } from '../../services/SoundService';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { iconMap } from '../../utils/iconUtils';
import { GameState } from '../../domain/entities';
import { TeamBadge } from '../shared/TeamBadge';
import './Navigation.css';
import { GAME_VERSION_NUMBER } from '../../version';

interface View {
    type: string;
    title: string;
    [key: string]: any;
}

interface NavigationProps {
    activeView: string;
    onNavigate: (view: View) => void;
    showDebug: boolean;
    hasUnreadNews: boolean;
    isMobileNavOpen: boolean;
    isAiModeVisible: boolean;
    gameState: GameState;
}

const baseNavItems = [
    // Match Preparation
    { id: 'play', label: 'Home', icon: 'home', colorHint: 'primary' },
    { id: 'inbox', label: 'Inbox', icon: 'inbox', colorHint: 'negative' },
    { id: 'assistant', label: 'Assistant', icon: 'assistant', colorHint: 'accent' },
    { id: 'competitions', label: 'Competitions', icon: 'competitions', colorHint: 'gold' },
    // Squad Management
    { id: 'team', label: 'My Team', icon: 'team', colorHint: 'positive' },
    { id: 'tactics', label: 'Tactics', icon: 'tactics', colorHint: 'info' },
    { id: 'training', label: 'Training', icon: 'training', colorHint: 'positive' },
    { id: 'youth', label: 'Youth', icon: 'youth', colorHint: 'positive' },
    { id: 'transfers', label: 'Transfers', icon: 'transfers', colorHint: 'info' },
    // Information
    { id: 'manager', label: 'Manager', icon: 'manager', colorHint: 'accent' },
    { id: 'history', label: 'History', icon: 'history', colorHint: 'gold' },
    { id: 'finances', label: 'Finances', icon: 'finances', colorHint: 'positive' },
    // Settings
    { id: 'game', label: 'Game', icon: 'game', colorHint: 'accent' },
];

const getColorHint = (colorHint: string): string => {
    const colorMap: Record<string, string> = {
        'primary': 'var(--accent-color)',
        'negative': 'var(--negative-color)',
        'gold': 'var(--gold-color)',
        'info': 'var(--info-color)',
        'positive': 'var(--positive-color)',
        'warning': 'var(--warning-color)',
        'accent': 'var(--accent-color)',
    };
    return colorMap[colorHint] || 'var(--accent-color)';
};


export const Navigation = ({ activeView, onNavigate, showDebug, hasUnreadNews, isMobileNavOpen, isAiModeVisible, gameState }: NavigationProps) => {
    const playerTeam = gameState.gameStarted && gameState.leagues[gameState.playerLeagueName]?.teams.find(t => t.name === gameState.playerTeamName);

    const navItems = [...baseNavItems];
    if (isAiModeVisible) {
        navItems.push({ id: 'aimode', label: 'AI Log', icon: 'aimode', colorHint: 'info' });
    }
    if (showDebug) {
        navItems.push({ id: 'debug', label: 'Debug', icon: 'debug', colorHint: 'warning' });
    }
    
    const handleNavigate = (viewType: string) => {
        soundService.playNavigate();
        const item = navItems.find(i => i.id === viewType);
        if (item) {
            onNavigate({ type: item.id, title: item.label });
        }
    };

    return (
        <nav 
            className={`navigation ${isMobileNavOpen ? 'mobile-nav-open' : ''}`}
        >
            {navItems.map(item => {
                const isInbox = item.id === 'inbox';
                const isMyTeam = item.id === 'team';
                const fontAwesomeIcon = iconMap[item.icon];
                const colorHint = 'colorHint' in item ? getColorHint((item as any).colorHint) : 'var(--accent-color)';
                const isActive = activeView === item.id;

                return (
                    <button
                        key={item.id}
                        data-nav-id={item.id}
                        className={`nav-button ${isActive ? 'active' : ''} ${isInbox && hasUnreadNews ? 'has-unread' : ''}`}
                        onClick={() => handleNavigate(item.id)}
                        title={item.label}
                    >
                        {isMyTeam && playerTeam ? (
                            <TeamBadge
                                badge={playerTeam.badge}
                                color={isActive ? 'var(--secondary-accent-color)' : playerTeam.colors.primary}
                                size="sm"
                            />
                        ) : fontAwesomeIcon ? (
                            <FontAwesomeIcon icon={fontAwesomeIcon} style={{ color: isActive ? 'var(--secondary-accent-color)' : colorHint }} />
                        ) : (
                            <span role="img" aria-label={item.label}>{item.icon}</span>
                        )}
                        <span className="nav-button-label">{item.label}</span>
                        {isInbox && hasUnreadNews && <div className="notification-dot"></div>}
                    </button>
                );
            })}
            <div className="nav-version-info">
                v{GAME_VERSION_NUMBER}
            </div>
        </nav>
    );
};